import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, FormControl, Select, MenuItem,
  CircularProgress, Paper, IconButton, Tooltip, ToggleButtonGroup, ToggleButton, Button, Badge,
  Popover, TextField,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import ViewDayIcon from '@mui/icons-material/ViewDay';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import CalendarGrid from '../components/CalendarGrid';
import MultiDoctorDayView from '../components/MultiDoctorDayView';
import WaitlistPanel from '../components/WaitlistPanel';
import AppointmentDialog from '../components/AppointmentDialog';
import SelfUpdateLinkDialog from '../components/SelfUpdateLinkDialog';
import { getDoctors, exportAppointments, printDailyAppointments, getWaitlist } from '../services/api';
import { saveAs } from 'file-saver';
import { useAuth } from '../hooks/useAuth';
import { useClinicFeatures } from '../hooks/useClinicFeatures';
import { useNotification } from '../hooks/useNotification';

// Recordar el último médico elegido en la agenda, por usuario — antes
// siempre se mostraba el primero de la lista al entrar, y con médicos que
// casi no tienen turnos apareciendo primero (orden alfabético/de alta, no
// de uso), recepción tenía que cambiar de médico todas las veces.
const lastDoctorKey = (userId) => `vitanotas_last_doctor_${userId}`;

const Dashboard = () => {
  const { user, isMedico, doctorId, isAdmin, isRecepcionista } = useAuth();
  const { features } = useClinicFeatures();
  const notify = useNotification();
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [selectedDoctorData, setSelectedDoctorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('single'); // 'single' | 'multi'
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const canManageWaitlist = (isAdmin || isRecepcionista) && features?.waitlist;
  // "Nuevo turno" en la Agenda — antes el único modo de crear un turno acá
  // era hacer click en un hueco vacío de la grilla, nada obvio para
  // usuarios con poca destreza tecnológica (pedido explícito del usuario
  // 2026-10-19). El médico ya está elegido en el selector de arriba, así
  // que no hace falta mini-form (a diferencia de /turnos, que no tiene
  // ningún médico implícito): el diálogo abre directo con el médico
  // actual y la hora de partida arbitraria 10:00 de hoy, igual que ya hace
  // CalendarGrid.js al clickear un hueco vacío.
  const canCreateAppointment = isAdmin || isRecepcionista || isMedico;
  const [newApptDialogOpen, setNewApptDialogOpen] = useState(false);
  // "Crear paciente por link" también en la Agenda — pedido explícito del
  // usuario (2026-10-20), mismo diálogo compartido que en Pacientes/Inicio.
  const [newPatientLinkOpen, setNewPatientLinkOpen] = useState(false);
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
  // Objeto estable mientras el diálogo está abierto — AppointmentDialog
  // queda siempre montado (ver más abajo), así que un literal inline acá
  // cambiaría de identidad en cada render del padre y re-dispararía el
  // useEffect que inicializa el formulario en cada re-render. La hora fija
  // 10:00 es arbitraria a propósito — si cae fuera del horario del médico,
  // AppointmentDialog ya maneja ese caso mostrando el popup de "¿crear como
  // sobreturno?" (ver AppointmentDialog.js) sin perder los datos del
  // paciente ya cargados en el formulario.
  const newApptSelectedEvent = useMemo(
    () => ({ start_time: `${new Date().toISOString().slice(0, 10)}T10:00:00` }),
    [newApptDialogOpen]
  );
  const openNewAppointment = () => setNewApptDialogOpen(true);

  useEffect(() => {
    // Antes un médico solo veía/cargaba su propio perfil acá, sin la lista
    // completa de doctors — el Dashboard nunca le ofrecía el selector de
    // médico. Pedido explícito del usuario (2026-10-09): acceso total a la
    // agenda de cualquier médico de la clínica (ej. agendar un turno para
    // un colega), mismo selector que ya usa admin/recepcionista. La única
    // diferencia real para un médico es el default inicial: arranca en SU
    // PROPIA agenda (no en localStorage ni en el primer médico de la
    // lista), porque sigue siendo lo que más va a mirar día a día.
    getDoctors({ limit: 500 })
      .then(r => {
        const items = r.data?.items || [];
        setDoctors(items);
        if (items.length > 0) {
          let initial;
          if (isMedico && doctorId) {
            initial = items.find(d => d.id === doctorId) || items[0];
          } else {
            let saved = null;
            try {
              const savedId = user?.id && localStorage.getItem(lastDoctorKey(user.id));
              saved = savedId && items.find(d => String(d.id) === savedId);
            } catch { /* localStorage no disponible (modo privado, etc.) */ }
            initial = saved || items[0];
          }
          setSelectedDoctor(initial.id);
          setSelectedDoctorData(initial);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isMedico, doctorId, user?.id]);

  // Sync selectedDoctorData when admin changes the selector
  const handleDoctorChange = (id) => {
    setSelectedDoctor(id);
    setSelectedDoctorData(doctors.find(d => d.id === id) || null);
    try {
      if (user?.id) localStorage.setItem(lastDoctorKey(user.id), String(id));
    } catch { /* localStorage no disponible (modo privado, etc.) */ }
  };

  const refreshWaitlistCount = useCallback(() => {
    if (!canManageWaitlist || !selectedDoctor) { setWaitlistCount(0); return; }
    getWaitlist({ doctor_id: selectedDoctor, status: 'activo' })
      .then(r => setWaitlistCount((r.data || []).length))
      .catch(() => setWaitlistCount(0));
  }, [canManageWaitlist, selectedDoctor]);

  useEffect(() => { refreshWaitlistCount(); }, [refreshWaitlistCount]);

  const handleExport = async () => {
    try {
      const params = selectedDoctor ? { doctor_id: selectedDoctor } : {};
      const response = await exportAppointments(params);
      let filename = 'turnos.xlsx';
      const disposition = response.headers['content-disposition'] || response.headers['Content-Disposition'] || '';
      if (disposition) {
        const part = disposition.split('filename=')[1];
        if (part) filename = part.trim().replace(/^["']|["']$/g, '');
      }
      saveAs(new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }), filename);
    } catch {
      notify('No se pudo descargar el archivo', 'error');
    }
  };

  // Imprimir turnos del día — pedido explícito del usuario (2026-10-30):
  // un papel "por las dudas" para recepción con más info que el Excel
  // (tipo de turno, pago, notas). Fecha por defecto: hoy. No depende de la
  // fecha que esté mostrando el calendario (CalendarGrid no la expone al
  // padre) — el popover deja elegir otro día si hace falta.
  const [printAnchorEl, setPrintAnchorEl] = useState(null);
  const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const [printDate, setPrintDate] = useState(todayStr());

  const handlePrintDaily = async () => {
    try {
      const params = { date: printDate };
      if (selectedDoctor) params.doctor_id = selectedDoctor;
      const response = await printDailyAppointments(params);
      let filename = `Turnos_${printDate}.pdf`;
      const disposition = response.headers['content-disposition'] || response.headers['Content-Disposition'] || '';
      if (disposition) {
        const part = disposition.split('filename=')[1];
        if (part) filename = part.trim().replace(/^["']|["']$/g, '');
      }
      saveAs(new Blob([response.data], { type: 'application/pdf' }), filename);
      setPrintAnchorEl(null);
    } catch {
      notify('No se pudo generar el PDF', 'error');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, height: 'calc(100vh - 82px)' }}>

      {/* ── Header ── */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 1.5, mb: 1.5, pb: 1.5,
        borderBottom: '1px solid', borderColor: 'divider',
      }}>
        {/* Izquierda: qué estoy mirando — lo primero que se decide al entrar.
            flex:1 en mobile para que el selector de médico (flex:1 también)
            tenga espacio real para crecer hasta el toggle individual/multi,
            en vez de dejar un hueco vacío en el medio. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', flex: { xs: 1, sm: 'none' } }}>
          {/* Antes este selector era exclusivo de admin/recepcionista — un
              médico solo veía su propia agenda, sin forma de elegir otra.
              Pedido explícito del usuario (2026-10-09): un médico también
              puede agendar en la agenda de un colega, mismo selector que ya
              usaba el resto de los roles (no se inventa uno nuevo). La
              vista multi-médico (abajo) queda igual que antes, solo para
              admin/recepcionista — no fue parte de este pedido y agregarla
              sería una extensión no pedida. */}
          {viewMode === 'single' && (
            // flex:1 en mobile — antes quedaba a ancho fijo con un hueco
            // vacío entre el selector y el toggle individual/multi a la
            // derecha. Pedido explícito del usuario 2026-10-30.
            <FormControl size="small" sx={{ minWidth: { xs: 160, sm: 220 }, maxWidth: { xs: 'none', sm: 'none' }, flex: { xs: 1, sm: 'none' } }}>
              <Select
                value={selectedDoctor}
                onChange={(e) => handleDoctorChange(e.target.value)}
                sx={{ bgcolor: 'background.paper', borderRadius: 1.5, fontWeight: 600 }}
                // Sin esto, el valor CERRADO del Select renderiza el mismo
                // contenido de 2 líneas (nombre + especialidad) que cada
                // MenuItem de la lista desplegada — ocupando doble alto
                // innecesariamente cuando está cerrado. La especialidad
                // sigue viéndose al abrir el desplegable, solo se saca de
                // acá. Bug real reportado con captura 2026-10-29.
                renderValue={(id) => {
                  const d = doctors.find((x) => x.id === id);
                  return d ? `${d.name}${isMedico && d.id === doctorId ? ' (yo)' : ''}` : '';
                }}
              >
                {doctors.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                        {d.name}
                        {isMedico && d.id === doctorId && ' (yo)'}
                      </Typography>
                      {d.specialty && (
                        <Typography variant="caption" color="text.secondary">{d.specialty}</Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {viewMode === 'multi' && (
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              Vista de {doctors.length} profesionales
            </Typography>
          )}

          {isMedico && !doctorId && (
            <Typography variant="body2" color="error.main">
              Sin perfil médico vinculado
            </Typography>
          )}

          {!isMedico && doctors.length > 1 && (
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, v) => { if (v) setViewMode(v); }}
              size="small"
              sx={{
                height: 32,
                // Mismo estilo "pill" azul que el resto de los toggles de la
                // agenda (Detallada/Resumen semanal, Semana/Día de FullCalendar).
                '& .MuiToggleButton-root': {
                  px: 1.5,
                  border: (theme) => `1px solid ${theme.palette.primary.light}`,
                  color: 'primary.main',
                  '&:hover': { bgcolor: (theme) => `${theme.palette.primary.light}22` },
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                  },
                },
              }}
            >
              <ToggleButton value="single">
                <Tooltip title="Vista individual (semana)">
                  <ViewWeekIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="multi">
                <Tooltip title="Vista multi-médico (día)">
                  <ViewDayIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>

        {/* Derecha: acciones ocasionales, menor jerarquía. En mobile (xs)
            solo íconos — con los 6 botones completos la fila no entraba en
            una pantalla chica y forzaba a que la agenda apareciera mucho
            más abajo. Pedido explícito del usuario 2026-10-30, señalando
            también que "crear paciente por link" es poco probable que se
            use desde el celular. width:100% + space-between en mobile:
            reparte los botones en todo el ancho disponible en vez de
            dejarlos amontonados a la izquierda con un hueco vacío al
            final (mismo pedido, segunda ronda). */}
        <Box sx={{
          display: 'flex', gap: { xs: 0.5, sm: 1 }, alignItems: 'center',
          width: { xs: '100%', sm: 'auto' },
          justifyContent: { xs: 'space-between', sm: 'flex-start' },
        }}>
          {canCreateAppointment && features?.patient_self_update && (
            <Tooltip title="Crear paciente por link">
              <Button
                size="small" variant="outlined" startIcon={<LinkIcon fontSize="small" />}
                onClick={() => setNewPatientLinkOpen(true)}
                sx={{ minWidth: { xs: 0, sm: 'auto' }, px: { xs: 1, sm: 2 }, '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } } }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Crear paciente por link</Box>
              </Button>
            </Tooltip>
          )}
          {viewMode === 'single' && canCreateAppointment && selectedDoctor && (
            // El más usado de la barra — pedido explícito del usuario
            // 2026-10-30: a diferencia del resto, este conserva el texto
            // también en mobile (sobra espacio porque los demás ya son
            // solo ícono).
            <Button
              size="small" variant="contained" startIcon={<AddIcon fontSize="small" />}
              onClick={openNewAppointment}
            >
              Nuevo turno
            </Button>
          )}
          {viewMode === 'single' && canManageWaitlist && selectedDoctor && (
            <Tooltip title="Lista de espera de este médico">
              <Button
                size="small"
                variant="text"
                color="inherit"
                startIcon={
                  <Badge badgeContent={waitlistCount} color="warning" invisible={waitlistCount === 0}>
                    <HourglassBottomIcon fontSize="small" />
                  </Badge>
                }
                onClick={() => setWaitlistOpen(true)}
                sx={{
                  textTransform: 'none', color: 'text.secondary',
                  minWidth: { xs: 0, sm: 'auto' }, px: { xs: 1, sm: 2 },
                  '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Lista de espera</Box>
              </Button>
            </Tooltip>
          )}

          {viewMode === 'single' && (
            <Tooltip title="Exportar Excel (semana actual)">
              <span>
                <IconButton onClick={handleExport} disabled={!selectedDoctor} size="small" sx={{ color: 'text.secondary' }}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {viewMode === 'single' && (
            <Tooltip title="Imprimir turnos del día">
              <IconButton
                onClick={(e) => { setPrintDate(todayStr()); setPrintAnchorEl(e.currentTarget); }}
                size="small"
                sx={{ color: 'text.secondary' }}
              >
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Popover
            open={Boolean(printAnchorEl)}
            anchorEl={printAnchorEl}
            onClose={() => setPrintAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, width: 240 }}>
              <Typography variant="subtitle2" fontWeight={700}>Imprimir turnos del día</Typography>
              <TextField
                type="date" size="small" label="Fecha" value={printDate}
                onChange={(e) => setPrintDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Typography variant="caption" color="text.secondary">
                {selectedDoctor ? 'Solo el médico seleccionado.' : 'Todos los médicos de la clínica.'}
              </Typography>
              <Button size="small" variant="contained" startIcon={<DownloadIcon fontSize="small" />} onClick={handlePrintDaily}>
                Descargar PDF
              </Button>
            </Box>
          </Popover>
        </Box>
      </Box>

      {/* ── Calendario ── */}
      {isMedico && !doctorId ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, color: 'text.secondary', gap: 1 }}>
          <Typography fontWeight={600}>Tu usuario no está vinculado a ningún médico.</Typography>
          <Typography variant="body2">
            Pedile al administrador que te asigne un perfil de médico en Gestión de usuarios.
          </Typography>
        </Box>
      ) : !isMedico && viewMode === 'multi' ? (
        <Paper elevation={0} sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
          <MultiDoctorDayView doctors={doctors} />
        </Paper>
      ) : !selectedDoctor && doctors.length === 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, color: 'text.secondary', gap: 2 }}>
          <Typography fontWeight={600}>Todavía no hay médicos creados en esta clínica.</Typography>
          <Typography variant="body2">Creá el primer médico para empezar a agendar turnos.</Typography>
          {isAdmin && (
            <Button variant="contained" component={RouterLink} to="/doctors">
              Crear médico
            </Button>
          )}
        </Box>
      ) : !selectedDoctor ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: 'text.secondary' }}>
          <Typography>Seleccioná un profesional para ver su agenda.</Typography>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          <CalendarGrid key={calendarRefreshKey} doctorId={selectedDoctor} />
        </Paper>
      )}

      {canManageWaitlist && (
        <WaitlistPanel
          open={waitlistOpen}
          onClose={() => { setWaitlistOpen(false); refreshWaitlistCount(); }}
          doctorId={selectedDoctor}
          doctorName={selectedDoctorData?.name}
        />
      )}

      {/* Montado siempre (igual patrón que CalendarGrid.js) — solo se
          alterna `open`. Antes se montaba/desmontaba con newApptDialogOpen,
          y como startDateTime se inicializa en un useEffect que corre
          DESPUÉS del primer render, el primer click en "Crear turno" podía
          caer antes de que el formulario tuviera fecha/hora, abortando en
          silencio (handleSubmit ve startDateTime null). El usuario tenía
          que apretar el botón dos veces. Bug real reportado 2026-10-29. */}
      <AppointmentDialog
        open={newApptDialogOpen}
        onClose={() => setNewApptDialogOpen(false)}
        isEdit={false}
        selectedEvent={newApptSelectedEvent}
        doctorId={selectedDoctor}
        doctor={selectedDoctorData}
        patients={[]}
        setPatients={() => {}}
        onSaved={() => { setNewApptDialogOpen(false); setCalendarRefreshKey(k => k + 1); }}
      />

      <SelfUpdateLinkDialog
        open={newPatientLinkOpen}
        onClose={() => setNewPatientLinkOpen(false)}
        patientId={null}
        patientPhone={null}
      />
    </Box>
  );
};

export default Dashboard;
